import { Link } from "react-router-dom";
import { AlertTriangle, Check, FolderPlus, Globe2, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { RATINGS, RATING_SHORT, WORK_STATUS, edited, fmt, hasWarnings, typeLabel } from "@/lib/writing";

export const KindBadge = ({ w }) => (
  <span className={`self-start w-fit font-code text-[10px] uppercase tracking-[0.18em] rounded-full border px-2 py-0.5 ${w.kind === "fanfiction" ? "border-violet-400/40 text-violet-200 bg-violet-500/10" : "border-amber-500/40 text-amber-200 bg-amber-500/10"}`} data-testid="work-kind-badge">
    {w.kind === "fanfiction" ? "Fanfiction" : "Original"}{typeLabel(w) ? ` · ${typeLabel(w)}` : ""}
  </span>
);

export const RatingBadge = ({ rating }) => {
  const r = RATINGS.find((x) => x[0] === rating) || RATINGS[0];
  return <span title={r[1]} className={`font-code text-[10px] font-bold rounded border px-1.5 py-0.5 ${r[2]}`} data-testid="work-rating-badge">{RATING_SHORT[r[0]]}</span>;
};

export const StatusLine = ({ w, total, published, compact }) => {
  const [label, cls] = WORK_STATUS[w.status] || WORK_STATUS.draft;
  return (
    <span className="inline-flex items-center gap-2 text-[11px]" data-testid="work-status-line">
      <span className={`whitespace-nowrap rounded-full border px-2 py-0.5 font-medium ${cls}`} data-testid="work-status-badge">{label}</span>
      <span className={`whitespace-nowrap font-code text-slate-500 ${compact ? "hidden sm:inline" : ""}`}>{published} of {total} published</span>
    </span>
  );
};

const Chips = ({ items, cls, max = 4 }) => items.length > 0 && (
  <div className="flex flex-wrap gap-1">{items.slice(0, max).map((t) => <span key={t} className={`text-[11px] rounded-full border px-2 py-0.5 ${cls}`}>{t}</span>)}
    {items.length > max && <span className="text-[11px] text-slate-500 px-1">+{items.length - max}</span>}</div>
);

const CardMenu = ({ w, collections, onToggle }) => {
  const manual = collections.filter((c) => c.kind === "manual");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="absolute top-4 right-4 z-10 p-1.5 rounded-md text-slate-500 hover:text-amber-300 hover:bg-veil/5" data-testid={`work-menu-${w.id}`}><MoreHorizontal className="w-4 h-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-panel border-line w-60">
        <DropdownMenuLabel className="text-xs text-slate-500 flex items-center gap-1.5"><FolderPlus className="w-3.5 h-3.5" /> Hand-picked collections</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {manual.length === 0 && <p className="px-2 py-1.5 text-xs text-slate-500">No hand-picked collections yet.</p>}
        {manual.map((c) => {
          const on = c.work_ids.includes(w.id);
          return <DropdownMenuItem key={c.id} onSelect={() => onToggle(c, w, !on)} data-testid={`work-menu-collection-${c.id}`}><Check className={`w-3.5 h-3.5 mr-2 ${on ? "text-amber-400" : "opacity-0"}`} />{c.name}</DropdownMenuItem>;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const WorkCard = ({ w, i, collections, onToggle }) => (
  <div className="rise relative group" style={{ animationDelay: `${i * 50}ms` }}>
    <CardMenu w={w} collections={collections} onToggle={onToggle} />
    <Link to={`/writing/${w.id}`} data-testid={`work-card-${w.id}`}
      className="flex flex-col h-full rounded-2xl border border-line bg-panel p-6 hover:-translate-y-1 hover:border-amber-500/40 transition-[transform,border-color] duration-300">
      <div className="flex items-center gap-2 pr-8">
        <KindBadge w={w} /><RatingBadge rating={w.rating} />
        {hasWarnings(w) && <AlertTriangle className="w-3.5 h-3.5 text-rose-300" title="Has warnings" data-testid="work-warning-marker" />}
      </div>
      <h3 className={`font-display text-2xl mt-3 group-hover:text-amber-300 transition-colors line-clamp-2 ${w.title === "Untitled" ? "text-slate-400 italic" : "text-amber-50"}`}>{w.title}</h3>
      <div className="mt-2"><StatusLine w={w} total={w.chapter_count} published={w.published} /></div>
      <p className="text-sm text-slate-400 mt-3 line-clamp-3 flex-1">{w.summary || "No summary yet."}</p>
      <div className="space-y-1.5 mt-4">
        <Chips items={w.fandoms} cls="border-violet-400/30 text-violet-200" />
        <Chips items={w.relationships} cls="border-pink-400/30 text-pink-200" max={3} />
        <Chips items={w.characters} cls="border-line2 text-slate-300" />
        <Chips items={w.tags} cls="border-line text-slate-500" max={5} />
      </div>
      <div className="mt-5 pt-4 border-t border-line flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-code text-slate-500">
        <span data-testid={`work-chapters-${w.id}`}>{w.chapter_count} {w.chapter_count === 1 ? "chapter" : "chapters"}</span>
        <span data-testid={`work-words-${w.id}`}>{fmt(w.words)} words</span>
        <span>Edited {edited(w.updated_at)}</span>
        {w.world_name && <span className="flex items-center gap-1 text-amber-500/70"><Globe2 className="w-3 h-3" />{w.world_name}</span>}
      </div>
    </Link>
  </div>
);
